// ── Gastos Tab ────────────────────────────────────────────────────────────────
import { getTrip }                    from '@/store/tripState';
import { saveTrip }                   from '@/services/sync.service';
import { openModal }                  from '@/ui/modal';
import { toastSuccess }               from '@/ui/toast';
import { toArr, uid, g, gf,
         fmtMask, parseMask, fmt,
         sanitize }                   from '@/helpers';
import { EXPENSE_CATS, EXPENSE_CAT_ICONS,
         COMMON_CURRENCIES }          from '@/constants';
import { getPayerName,
         getTravelerOptions }         from '@/tabs/viagem';
import type { Expense, ExpenseCat }   from '@/types';

// ── Get currencies configured for this trip ───────────────────
function getTripCurrencies(): string[] {
  const trip = getTrip();
  const configured = trip.currencies ?? [];
  return configured.length ? configured : ['BRL'];
}

// ── Currency options for select ───────────────────────────────
function currencyOptions(selected = ''): string {
  const active = getTripCurrencies();
  return active.map(code => {
    const info = COMMON_CURRENCIES.find(c => c.code === code);
    return `<option value="${code}" ${code === selected ? 'selected' : ''}>${code}${info ? ' — ' + info.label : ''}</option>`;
  }).join('');
}

// ── Render ────────────────────────────────────────────────────
export function renderGastos(): void {
  const el    = document.getElementById('gastos-list');
  const sumEl = document.getElementById('gastos-summary');
  if (!el) return;

  const search = ((document.getElementById('gastos-search') as HTMLInputElement)?.value ?? '').toLowerCase();
  const catF   = (document.getElementById('gastos-cat-filter') as HTMLSelectElement)?.value ?? '';
  const curF   = (document.getElementById('gastos-cur-filter') as HTMLSelectElement)?.value ?? '';

  let arr = toArr(getTrip().expenses ?? {}) as Expense[];
  arr.sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));

  // Filters
  if (catF)   arr = arr.filter(e => e.cat === catF);
  if (curF)   arr = arr.filter(e => e.currency === curF);
  if (search) arr = arr.filter(e =>
    (e.desc ?? '').toLowerCase().includes(search) ||
    (e.notes ?? '').toLowerCase().includes(search)
  );

  // Summary by currency
  if (sumEl) {
    const byCur: Record<string, number> = {};
    arr.forEach(e => { byCur[e.currency] = (byCur[e.currency] ?? 0) + e.amount; });
    sumEl.innerHTML = Object.entries(byCur).length
      ? Object.entries(byCur).map(([cur, total]) => {
          const info = COMMON_CURRENCIES.find(c => c.code === cur);
          return `<div class="gastos-total-chip">
            <span class="gastos-total-cur">${info?.symbol ?? cur}</span>
            <span class="gastos-total-val">${total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            <span class="gastos-total-label">${cur}</span>
          </div>`;
        }).join('')
      : '<span style="color:var(--muted);font-size:12px">Nenhum gasto registrado.</span>';
  }

  if (!arr.length) {
    el.innerHTML = '<div class="empty">Nenhum gasto encontrado.</div>';
    return;
  }

  // Group by date
  const byDate: Record<string, Expense[]> = {};
  arr.forEach(e => { (byDate[e.date ?? 'sem data'] ??= []).push(e); });

  el.innerHTML = Object.entries(byDate)
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, items]) => {
      // Totals per currency for this day
      const dayTotals: Record<string, number> = {};
      items.forEach(e => { dayTotals[e.currency] = (dayTotals[e.currency] ?? 0) + e.amount; });
      const totalsHtml = Object.entries(dayTotals).map(([cur, total]) => {
        const info = COMMON_CURRENCIES.find(c => c.code === cur);
        return `<span class="gastos-day-total">${info?.symbol ?? cur} ${total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>`;
      }).join('');

      return `
        <div class="gastos-day-block">
          <div class="gastos-day-header">
            <span class="gastos-day-date">${fmt(date)}</span>
            <div class="gastos-day-totals">${totalsHtml}</div>
          </div>
          ${items.map(e => expenseRow(e)).join('')}
        </div>`;
    }).join('');
}

function expenseRow(e: Expense): string {
  const icon   = EXPENSE_CAT_ICONS[e.cat] ?? '📌';
  const payer  = e.pagador ? getPayerName(e.pagador) : '';
  const info   = COMMON_CURRENCIES.find(c => c.code === e.currency);
  const symbol = info?.symbol ?? e.currency;
  return `
    <div class="gastos-row">
      <div class="gastos-row-icon">${icon}</div>
      <div class="gastos-row-body">
        <div class="gastos-row-desc">${sanitize(e.desc ?? '—')}</div>
        <div class="gastos-row-meta">
          ${e.cat}${payer ? ' · ' + payer : ''}${e.notes ? ' · ' + sanitize(e.notes) : ''}
        </div>
      </div>
      <div class="gastos-row-amount">
        <div class="gastos-row-value">${symbol} ${e.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
        <div style="font-size:10px;color:var(--muted)">${e.currency}</div>
      </div>
      <div class="card-actions" style="flex-direction:column;gap:2px">
        <button class="icon-btn" onclick="window.__openGastoModal('${e.id}')">✏</button>
        <button class="icon-btn del" onclick="window.__delItem('expenses','${e.id}')">×</button>
      </div>
    </div>`;
}

// ── Open gasto modal ──────────────────────────────────────────
export function openGastoModal(editId?: string): void {
  const trip     = getTrip();
  const expenses = trip.expenses ?? {};
  const item     = editId ? expenses[editId] as Expense | undefined : undefined;
  const p        = (f: keyof Expense, def = '') => {
    const raw = item ? (item[f] !== undefined ? String(item[f]) : def) : def;
    return sanitize(raw);
  };
  const today    = new Date().toISOString().slice(0, 10);

  const catOpts = EXPENSE_CATS.map(c =>
    `<option value="${c}" ${p('cat') === c ? 'selected' : ''}>${EXPENSE_CAT_ICONS[c]} ${c}</option>`
  ).join('');

  openModal(editId ? 'Editar gasto' : 'Registrar gasto', `
    <div class="fg">
      <label>Descrição</label>
      <input id="f-gasto-desc" value="${p('desc')}" placeholder="Ex: Jantar no restaurante">
    </div>
    <div class="r2">
      <div class="fg">
        <label>Categoria</label>
        <select id="f-gasto-cat">${catOpts}</select>
      </div>
      <div class="fg">
        <label>Data</label>
        <input type="date" id="f-gasto-date" value="${p('date', today)}">
      </div>
    </div>
    <div class="r2">
      <div class="fg">
        <label>Moeda</label>
        <select id="f-gasto-currency">${currencyOptions(p('currency', getTripCurrencies()[0]))}</select>
      </div>
      <div class="fg">
        <label>Valor</label>
        <input type="text" inputmode="numeric" id="f-gasto-amount"
          value="${item?.amount ? fmtMask(item.amount) : ''}"
          oninput="window.__onBudgetInput(this)" placeholder="0,00">
      </div>
    </div>
    <div class="fg">
      <label>Quem pagou</label>
      <select id="f-gasto-pagador">${getTravelerOptions(p('pagador'))}</select>
    </div>
    <div class="fg">
      <label>Notas (opcional)</label>
      <input id="f-gasto-notes" value="${p('notes')}" placeholder="Detalhes adicionais">
    </div>`,
    async () => {
      const amount = parseMask((document.getElementById('f-gasto-amount') as HTMLInputElement)?.value ?? '0');
      if (!g('f-gasto-desc')) throw new Error('O campo "Descrição" é obrigatório.');
      if (!amount || amount <= 0) throw new Error('Informe um valor válido.');

      const id = editId ?? uid();
      const obj: Expense = {
        id,
        desc:     g('f-gasto-desc'),
        cat:      g('f-gasto-cat') as ExpenseCat,
        date:     g('f-gasto-date'),
        currency: g('f-gasto-currency'),
        amount,
        pagador:  g('f-gasto-pagador'),
        notes:    g('f-gasto-notes'),
      };
      await saveTrip(`expenses/${id}`, obj);
      toastSuccess('Gasto registrado!');
      renderGastos();
    }
  );
}

export function filterGastos(): void { renderGastos(); }
export function clearGastosFilters(): void {
  ['gastos-search','gastos-cat-filter','gastos-cur-filter'].forEach(id => {
    const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
    if (el) el.value = '';
  });
  renderGastos();
}
